/**
 * Stone Investment Project Card
 * 
 * Magazine-style project card with Stone Investment's luxury aesthetic:
 * - HorizonOverlay effect
 * - Cinematic transitions
 * - Editorial typography
 */

'use client';

import React from 'react';
import Image from 'next/image';

// Simplified project card that doesn't depend on external MagazineBannerCard
// Uses Stone Investment's greyscale aesthetic directly

export interface StoneProjectCardBaseProps {
  name: string;
  slug: string;
  tagline?: string;
  location?: string;
  image?: string;
  status?: 'PLANNING' | 'UNDER_CONSTRUCTION' | 'COMPLETED' | 'SELLING';
  href: string;
  className?: string;
}

export type MagazineBannerCardProps = StoneProjectCardBaseProps & {
  brandColors?: { primary?: string; accent?: string };
  variant?: 'default' | 'featured' | 'compact';
};

export type StoneProjectCardProps = MagazineBannerCardProps;

/**
 * StoneProjectCard - Magazine Banner Style
 * 
 * Stone Investment themed project card with greyscale palette
 * for an editorial, high-end property development presentation.
 */
export function StoneProjectCard({
  name,
  tagline,
  location,
  image,
  status,
  href,
  className = '',
}: StoneProjectCardProps) {
  const getStatusLabel = (s?: string) => {
    switch (s) {
      case 'PLANNING': return 'In Planning';
      case 'UNDER_CONSTRUCTION': return 'Under Construction';
      case 'COMPLETED': return 'Completed';
      case 'SELLING': return 'Now Selling';
      default: return '';
    }
  };

  return (
    <a href={href} className={`group block ${className}`}>
      <div className="relative aspect-[16/9] overflow-hidden bg-stone-200 mb-4">
        {image && (
          <Image
            src={image}
            alt={name}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105"
          />
        )}
        {status && (
          <div className="absolute top-4 left-4 px-3 py-1.5 bg-stone-900 text-[10px] uppercase tracking-[0.2em] text-white font-sans">
            {getStatusLabel(status)}
          </div>
        )}
      </div>
      <div className="border-t border-stone-200 pt-4">
        {location && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
            {location}
          </p>
        )}
        <h3 className="font-serif text-2xl text-stone-900 mb-2 group-hover:italic transition-all">
          {name}
        </h3>
        {tagline && (
          <p className="text-sm text-stone-600 font-sans">{tagline}</p>
        )}
      </div>
    </a>
  );
}

export default StoneProjectCard;
